import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import { resetAllState } from "./resetAllState";
import { fetchCartProducts } from "../../services/thunks/CartThunk";
import VariantLineKeyHelper from "../../services/helpers/VariantLineKeyHelper";

/* ── sessionStorage helpers (scoped by businessId) ─────────────────── */
const CART_STORAGE_KEY = "nine_collection_cart";
const CART_BIZ_KEY = "nine_collection_biz";

function loadCartFromSession(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCartToSession(items: Record<string, number>) {
  try {
    if (Object.keys(items).length === 0) {
      sessionStorage.removeItem(CART_STORAGE_KEY);
      sessionStorage.removeItem(CART_BIZ_KEY);
    } else {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    }
  } catch { /* quota exceeded — silently fail */ }
}

function getStoredBusinessId(): string | null {
  return sessionStorage.getItem(CART_BIZ_KEY);
}

function setStoredBusinessId(bizId: string) {
  sessionStorage.setItem(CART_BIZ_KEY, bizId);
}

/* ── Slice ─────────────────────────────────────────────────────────── */

interface CartState {
  items: Record<string, number>;
  /** The businessId this collection belongs to. Cleared on business switch. */
  businessId: string | null;
  products: ProductDetailsModal[];
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const restoredBizId = getStoredBusinessId();
const restoredItems = restoredBizId ? loadCartFromSession() : {};

const initialState: CartState = {
  items: restoredItems,
  businessId: restoredBizId,
  products: [],
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    /**
     * Bind the cart to a specific business. If the businessId changes,
     * the cart is automatically cleared so collections don't leak across
     * businesses. Call this when entering a business page.
     */
    setCartBusiness(state, action: PayloadAction<string>) {
      const newBizId = action.payload;
      if (state.businessId && state.businessId !== newBizId) {
        // Entering a different business → reset cart
        state.items = {};
        state.products = [];
      }
      state.businessId = newBizId;
      setStoredBusinessId(newBizId);
      saveCartToSession(state.items);
    },
    addToCart(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.items[id] = (state.items[id] ?? 0) + 1;
      saveCartToSession(state.items);
    },
    removeFromCart(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (!state.items[id]) return;
      if (state.items[id] === 1) {
        delete state.items[id];
      } else {
        state.items[id] -= 1;
      }
      saveCartToSession(state.items);
    },
    clearCart(state) {
      state.items = {};
      state.products = [];
      state.businessId = null;
      saveCartToSession(state.items);
    },
    clearCartProducts(state) {
      state.products = [];
    },
    removeCartItem(state, action: PayloadAction<string>) {
      const id = action.payload;
      delete state.items[id];
      saveCartToSession(state.items);
    },
    setCartItemQty(state, action: PayloadAction<{ id: string; qty: number }>) {
      const { id, qty } = action.payload;
      if (qty <= 0) {
        delete state.items[id];
      } else {
        state.items[id] = qty;
      }
      saveCartToSession(state.items);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartProducts.pending, (state) => {
        state.loadingMap.fetchCartProducts = "Loading products...";
        state.errorMap.fetchCartProducts = null;
        state.successMap.fetchCartProducts = false;
      })
      .addCase(fetchCartProducts.fulfilled, (state, action) => {
        state.loadingMap.fetchCartProducts = null;
        state.successMap.fetchCartProducts = true;
        state.products = action.payload;
      })
      .addCase(fetchCartProducts.rejected, (state, action) => {
        state.loadingMap.fetchCartProducts = null;
        state.errorMap.fetchCartProducts = action.payload as string;
      })
      .addCase(resetAllState, () => {
        // On full state reset, also clear storage
        saveCartToSession({});
        return { ...initialState, items: {}, businessId: null };
      });
  },
});

export const {
  setCartBusiness,
  addToCart,
  removeFromCart,
  clearCart,
  clearCartProducts,
  removeCartItem,
  setCartItemQty,
} = cartSlice.actions;

export const selectCartItems = (state: any): Record<string, number> =>
  state.cart.items;

export const selectCartCount = (state: any): number =>
  Object.values(state.cart.items as Record<string, number>).reduce(
    (sum, qty) => sum + qty,
    0
  );

/**
 * `items` is keyed by cart LINE (bare productId, or productId~variantId —
 * see VariantLineKeyHelper), so a product with several variants in the cart
 * has several keys. This sums every line belonging to one productId —
 * useful anywhere that only knows the product, not which variant(s) of it
 * are in the cart (e.g. a product card's own quantity badge).
 */
export const selectCartQtyForProduct = (productId: string) => (state: any): number =>
  Object.entries(state.cart.items as Record<string, number>).reduce(
    (sum, [lineKey, qty]) =>
      VariantLineKeyHelper.parseLineKey(lineKey).productId === productId ? sum + qty : sum,
    0
  );

export const selectCartBusinessId = (state: any): string | null =>
  state.cart.businessId;

export const selectCartProducts = (state: any): ProductDetailsModal[] =>
  state.cart.products;

export const selectCartLoadingMap = (state: any) => state.cart.loadingMap;
export const selectCartErrorMap = (state: any) => state.cart.errorMap;

export default cartSlice.reducer;
